import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex } from 'theme-ui'
import { Loading, Button, Tooltip } from 'components'
import { InfoIcon } from 'components/Icon'
import { ErrorMessage, Select } from 'components/Form'
import { Textarea } from 'components/Form/Textarea'
import { useConfirmation } from 'hooks/useConfirmation'
import { useAuth } from 'providers'
import * as access from 'types/access'
import { publishSubtitles } from 'api/subtitles/publishSubtitles'
import { saveSubtitles } from 'api/subtitles/saveSubtitles'
import { SubtitleTimestampField } from 'videoReview/components/SubtitleTimestampField'
import { Cue, isValidTimestamp, parseVtt } from 'videoReview/utils/parseVtt'

interface Subtitle {
  lang: string
  label: string
  url: string
}

interface Props {
  videoId: string
  subtitles: Subtitle[]
  onSeek?: (seconds: number) => void
  onCuesChange?: (lang: string, cues: Cue[]) => void
}

function timestampToSeconds(ts: string): number {
  const parts = ts.split(':')
  if (parts.length === 3) {
    return Number(parts[0]) * 3600 + Number(parts[1]) * 60 + Number(parts[2])
  }
  if (parts.length === 2) {
    return Number(parts[0]) * 60 + Number(parts[1])
  }
  return 0
}

export function SubtitleEditor({
  videoId,
  subtitles,
  onSeek,
  onCuesChange,
}: Props) {
  const [selectedLang, setSelectedLang] = useState<string>('')
  const [cues, setCues] = useState<Cue[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editingCueIndex, setEditingCueIndex] = useState<number | null>(null)
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null)
  const [editingTimestampIndex, setEditingTimestampIndex] = useState<
    number | null
  >(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const lastSavedJsonRef = useRef<string | null>(null)

  useEffect(() => {
    if (openMenuIndex === null) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuIndex(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [openMenuIndex])

  useEffect(() => {
    if (subtitles.length > 0 && !selectedLang) {
      setSelectedLang(subtitles[0].lang)
    }
  }, [subtitles, selectedLang])

  const selectedSubtitle = useMemo(
    () => subtitles.find((s) => s.lang === selectedLang),
    [subtitles, selectedLang]
  )

  useEffect(() => {
    if (!selectedSubtitle) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setEditingCueIndex(null)
    setOpenMenuIndex(null)
    setEditingTimestampIndex(null)
    setSaveError(null)
    setLastSavedAt(null)
    lastSavedJsonRef.current = null
    fetch(selectedSubtitle.url)
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load subtitle (${r.status})`)
        return r.text()
      })
      .then((text) => {
        if (cancelled) return
        const parsed = parseVtt(text)
        setCues(parsed)
        // baseline for "dirty" detection — anything different from this triggers save
        lastSavedJsonRef.current = JSON.stringify(parsed)
      })
      .catch((e) => {
        if (!cancelled) setError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedSubtitle])

  useEffect(() => {
    if (selectedLang && cues.length > 0 && onCuesChange) {
      onCuesChange(selectedLang, cues)
    }
  }, [cues, selectedLang, onCuesChange])

  const updateCueText = (index: number, newText: string) => {
    setCues((prev) =>
      prev.map((c) => (c.index === index ? { ...c, text: newText } : c))
    )
  }

  const updateCueTimestamp = (
    index: number,
    field: 'start' | 'end',
    value: string
  ) => {
    setCues((prev) =>
      prev.map((c) => (c.index === index ? { ...c, [field]: value } : c))
    )
  }

  const handleTimestampBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null
    if (!next || !e.currentTarget.contains(next)) {
      setEditingTimestampIndex(null)
      maybeSaveDraft()
    }
  }

  const maybeSaveDraft = async () => {
    if (!selectedLang || cues.length === 0) return
    const currentJson = JSON.stringify(cues)
    if (currentJson === lastSavedJsonRef.current) return
    setSaving(true)
    setSaveError(null)
    try {
      const result = await saveSubtitles({
        id: videoId,
        lang: selectedLang,
        cues,
      })
      if (!result.success) {
        throw new Error(result.error || 'Save failed')
      }
      lastSavedJsonRef.current = currentJson
      setLastSavedAt(new Date())
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const askPublishConfirmation = useConfirmation({
    header: 'Publish to production?',
    body: `This will replace the live ${
      selectedSubtitle?.label || selectedSubtitle?.lang
    } subtitles for all app users. The change takes effect immediately and cannot be undone.`,
    confirmText: 'Publish',
  })

  const { hasAccess } = useAuth()
  const canPublish = hasAccess(
    access.Groups.TMATT,
    access.TMATT.PUBLISH_SUBTITLES
  )

  const [publishing, setPublishing] = useState(false)
  const [publishError, setPublishError] = useState<string | null>(null)

  const handlePublish = async () => {
    const confirmed = await askPublishConfirmation()
    if (!confirmed) return
    setPublishing(true)
    setPublishError(null)
    try {
      const result = await publishSubtitles({
        id: videoId,
        lang: selectedLang,
        cues,
      })
      if (!result.success) {
        throw new Error(result.error || 'Publish failed')
      }
    } catch (e) {
      setPublishError(e instanceof Error ? e.message : String(e))
    } finally {
      setPublishing(false)
    }
  }

  if (subtitles.length === 0) {
    return (
      <Box sx={{ p: 3, color: 'gray' }}>
        No subtitles available for this video.
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '70vh',
        bg: 'white',
        borderRadius: 8,
        border: 'solid 1px',
        borderColor: '#e0e0e0',
      }}
    >
      <Flex
        sx={{
          p: 3,
          borderBottom: 'solid 1px',
          borderColor: '#e0e0e0',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3,
        }}
      >
        <Box sx={{ fontWeight: 'bold', fontSize: 2, ml: 2 }}>Transcript</Box>
        <Flex sx={{ alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: '0 0 auto', minWidth: 200 }}>
            <Select
              value={selectedLang}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelectedLang(e.target.value)
              }
            >
              {subtitles.map((s) => (
                <option key={s.lang} value={s.lang}>
                  {s.label} ({s.lang})
                </option>
              ))}
            </Select>
          </Box>
          <Tooltip
            width={260}
            message={
              <Flex sx={{ flexDirection: 'column', gap: 1 }}>
                <Box>
                  1 - Select a language in the list below to edit its transcript
                </Box>
                <Box>
                  2 - To view that same language as captions in the video
                  player, use the checkbox above the player.
                </Box>
              </Flex>
            }
            tooltipSx={{ right: 0, top: '100%', mt: 1 }}
          >
            <Box
              sx={{
                color: 'gray',
                display: 'inline-flex',
              }}
            >
              <InfoIcon />
            </Box>
          </Tooltip>
        </Flex>
      </Flex>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : cues.length === 0 ? (
          <Box sx={{ p: 3, color: 'gray' }}>
            No cues found in this subtitle.
          </Box>
        ) : (
          cues.map((cue) => (
            <Box
              key={cue.index}
              sx={{
                py: 2,
                px: 2,
                borderBottom: 'solid 1px',
                borderColor: '#f0f0f0',
              }}
            >
              <Flex
                sx={{
                  fontSize: 0,
                  color: 'gray',
                  mb: 1,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                {editingTimestampIndex === cue.index ? (
                  <Flex
                    onBlur={handleTimestampBlur}
                    sx={{ alignItems: 'center', gap: 1 }}
                  >
                    <SubtitleTimestampField
                      value={cue.start}
                      autoFocus
                      validate={isValidTimestamp}
                      onChange={(v) =>
                        updateCueTimestamp(cue.index, 'start', v)
                      }
                      sx={{
                        width: 110,
                        fontSize: 0,
                        py: 1,
                        px: 2,
                      }}
                    />
                    <span>→</span>
                    <SubtitleTimestampField
                      value={cue.end}
                      validate={isValidTimestamp}
                      onChange={(v) => updateCueTimestamp(cue.index, 'end', v)}
                      sx={{
                        width: 110,
                        fontSize: 0,
                        py: 1,
                        px: 2,
                      }}
                    />
                  </Flex>
                ) : (
                  <span>
                    {cue.start} → {cue.end}
                  </span>
                )}

                <Box sx={{ position: 'relative' }}>
                  <Box
                    as="button"
                    onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                    onClick={() =>
                      setOpenMenuIndex(
                        openMenuIndex === cue.index ? null : cue.index
                      )
                    }
                    title="More options"
                    aria-label="More options"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 22,
                      height: 22,
                      p: 0,
                      border: 'none',
                      bg: 'transparent',
                      color: 'gray',
                      cursor: 'pointer',
                      borderRadius: '50%',
                      fontSize: '16px',
                      lineHeight: 1,
                      '&:hover': { bg: '#e6e6e6', color: 'primary' },
                    }}
                  >
                    ⋮
                  </Box>
                  {openMenuIndex === cue.index && (
                    <Box
                      ref={menuRef}
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        mt: 1,
                        bg: 'white',
                        border: 'solid 1px',
                        borderColor: '#e0e0e0',
                        borderRadius: 4,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        minWidth: 180,
                        zIndex: 10,
                      }}
                    >
                      {onSeek && (
                        <Box
                          as="button"
                          onClick={() => {
                            onSeek(timestampToSeconds(cue.start))
                            setOpenMenuIndex(null)
                          }}
                          sx={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            px: 3,
                            py: 2,
                            border: 'none',
                            bg: 'transparent',
                            cursor: 'pointer',
                            fontSize: 1,
                            color: 'text',
                            '&:hover': { bg: '#f5f5f5' },
                          }}
                        >
                          Jump to
                        </Box>
                      )}
                      <Box
                        as="button"
                        onClick={() => {
                          setEditingTimestampIndex(cue.index)
                          setOpenMenuIndex(null)
                        }}
                        sx={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          px: 3,
                          py: 2,
                          border: 'none',
                          bg: 'transparent',
                          cursor: 'pointer',
                          fontSize: 1,
                          color: 'text',
                          '&:hover': { bg: '#f5f5f5' },
                        }}
                      >
                        Edit timestamps
                      </Box>
                    </Box>
                  )}
                </Box>
              </Flex>
              {editingCueIndex === cue.index ? (
                <Textarea
                  value={cue.text}
                  autoFocus
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    updateCueText(cue.index, e.target.value)
                  }
                  onBlur={() => {
                    setEditingCueIndex(null)
                    maybeSaveDraft()
                  }}
                  rows={2}
                  sx={{ width: '100%', resize: 'vertical' }}
                />
              ) : (
                <Box
                  onClick={() => setEditingCueIndex(cue.index)}
                  sx={{
                    cursor: 'text',
                    whiteSpace: 'pre-wrap',
                    minHeight: '1.5em',
                    px: 2,
                    py: 1,
                    borderRadius: 4,
                    color: cue.text ? 'inherit' : 'gray',
                    '&:hover': {
                      bg: '#f5f5f5',
                    },
                  }}
                >
                  {cue.text || '(empty — click to edit)'}
                </Box>
              )}
            </Box>
          ))
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          p: 3,
          borderTop: 'solid 1px',
          borderColor: '#e0e0e0',
        }}
      >
        <Box sx={{ fontSize: 0, color: 'gray', flex: 1 }}>
          {publishError ? (
            <Box sx={{ color: 'red' }}>{publishError}</Box>
          ) : saveError ? (
            <Box sx={{ color: 'red' }}>Save failed: {saveError}</Box>
          ) : saving ? (
            <span>Saving…</span>
          ) : lastSavedAt ? (
            <span>Draft saved</span>
          ) : null}
        </Box>
        {canPublish && (
          <Button
            onClick={handlePublish}
            disabled={loading || publishing || cues.length === 0 || !!error}
          >
            {publishing ? 'Publishing…' : 'Publish'}
          </Button>
        )}
      </Box>
    </Box>
  )
}
